import { useState, useEffect, useCallback, useMemo } from 'react';
import { getAgencyTrends, getConsolidatedTrends, getAgencySummary } from '../services/oathTrendService';

/**
 * Transform raw YoY data (flat monthly rows spanning 2 years) into paired prev/curr format for the chart.
 * API returns up to 24 months of { year, month, monthName, liability, count }.
 * We pair them by month: previous year vs current year.
 */
const transformYoYData = (rawYoy) => {
  if (!rawYoy?.length) return [];

  // Determine current and previous year from the data
  const years = [...new Set(rawYoy.map((r) => r.year))].sort((a, b) => a - b);
  if (years.length === 0) return [];

  const currentYear = years[years.length - 1];
  const previousYear = years.length > 1 ? years[years.length - 2] : null;

  // Group by month
  const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  return MONTHS.map((monthName, idx) => {
    const monthNum = idx + 1;
    const prev = previousYear ? rawYoy.find((r) => r.year === previousYear && r.month === monthNum) : null;
    const curr = rawYoy.find((r) => r.year === currentYear && r.month === monthNum);
    return {
      month: monthName,
      prevLiability: prev?.liability || 0,
      currLiability: curr?.liability || 0,
      prevCount: prev?.count || 0,
      currCount: curr?.count || 0,
    };
  });
};

/**
 * Transform nested denomination data into flat rows for the stacked area chart.
 * API returns: [{ year, month, monthName, totalLiability, denominations: [{ denominationName, liability, count }] }]
 * Chart needs: [{ month, '$5,500': 71500, '$11,000': 132000, ... }]
 */
const transformDenominationData = (rawDenom) => {
  if (!rawDenom?.length) return { data: [], denominationKeys: [] };

  // Collect all unique denomination names across all months
  const denomSet = new Set();
  rawDenom.forEach((row) => {
    row.denominations?.forEach((d) => denomSet.add(d.denominationName));
  });
  const denominationKeys = [...denomSet];

  const data = rawDenom.map((row) => {
    const point = { month: row.monthName };
    denominationKeys.forEach((key) => {
      const match = row.denominations?.find((d) => d.denominationName === key);
      point[key] = match?.liability || 0;
    });
    return point;
  });

  return { data, denominationKeys };
};

/**
 * Hook for fetching and managing agency trend data + executive summary.
 * Call once in Dashboard.jsx and pass results to TrendsTab and SummaryTab as props.
 *
 * @param {{ agencyId: number, consolidated?: boolean }} options
 */
export const useTrends = ({ agencyId, consolidated = false }) => {
  const [trends, setTrends] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summaryError, setSummaryError] = useState(null);

  const fetchTrends = useCallback(async (id, useConsolidated) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const result = useConsolidated ? await getConsolidatedTrends(id) : await getAgencyTrends(id);
      if (result.success) {
        setTrends(result.data);
      } else {
        setError(result.error || 'Failed to load trends');
      }
    } catch (err) {
      setError('Failed to load trends');
      console.error('Fetch trends error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSummary = useCallback(async (id) => {
    if (!id) return;
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const result = await getAgencySummary(id);
      if (result.success) {
        setSummary(result.data);
      } else {
        setSummaryError(result.error || 'Failed to load summary');
      }
    } catch (err) {
      setSummaryError('Failed to load summary');
      console.error('Fetch summary error:', err);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (agencyId) {
      fetchTrends(agencyId, consolidated);
      fetchSummary(agencyId);
    }
  }, [agencyId, consolidated, fetchTrends, fetchSummary]);

  // Transform YoY data for the chart (paired prev/curr by month)
  const yoyChartData = useMemo(() => transformYoYData(trends?.yoy), [trends?.yoy]);

  // Transform denomination data for the stacked area chart
  const denominationChart = useMemo(() => transformDenominationData(trends?.denomination), [trends?.denomination]);

  return {
    // Raw trends data
    trends,
    // Transformed chart data
    yoyChartData,
    denominationChartData: denominationChart.data,
    denominationKeys: denominationChart.denominationKeys,
    historicalData: trends?.historical || [],
    // Executive summary (from /summary endpoint)
    summary,
    summaryLoading,
    summaryError,
    // Trends loading/error
    loading,
    error,
    // Refetch both
    refetch: () => {
      fetchTrends(agencyId, consolidated);
      fetchSummary(agencyId);
    },
  };
};
